import React, { useState, useEffect, useRef, useContext } from "react";
import { Prompt } from "react-router";
import moment from "moment";
import { useQuery, useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import Files from "react-butterfiles";
import CircularProgress from "@material-ui/core/CircularProgress";

import AddPhotoAlternateIcon from "@material-ui/icons/AddPhotoAlternate";
import CategoryIcon from "@material-ui/icons/Category";

import { endPoint, graphqlUrl } from "utils/config";
import { ThemeContext } from "index";
import ErrorPage from "commonComponents/errorPage/ErrorPage";
import PageHeader from "designV2/components/PageHeader";
import Button from "designV2/components/Button";
import Field from "designV2/components/Field";
import Panel from "designV2/components/Panel";
import EmptyState from "designV2/components/EmptyState";
import { MetricsSkeleton, TableSkeleton } from "designV2/components/Skeleton";
import "./productForm.css";

/* ------------------------------------------------------------------ *
 * GraphQL
 *
 * Shop-admin only — the shared commonComponents/addProduct/AddProduct.js
 * this replaces is also used by super admin (creating/editing a product
 * on a shop's behalf) and stays as-is for that; the extra branching for
 * a `shop` argument and a second set of "ByAdmin" mutations isn't needed
 * here.
 * ------------------------------------------------------------------ */

const GET_FORM_CONTEXT = gql`
  query getShopProductFormContext {
    getShopByShopAdmin {
      _id
      shopMenu {
        _id
        subMenus {
          _id
          name
        }
      }
      rootCategory {
        _id
        title
      }
    }
    getReqCarTypes(filters: { tripType: DELIVERY }) {
      _id
      name
    }
  }
`;

const GET_ATTRIBUTE_GROUPS = gql`
  query getAttributeGroups($rootCategory: ID) {
    getAttributeGroups(filters: { rootCategory: $rootCategory }) {
      _id
      name
      attributes {
        _id
        name
      }
    }
  }
`;

const UPLOAD_FILE = gql`
  mutation uploadFile($file: Upload!, $folderName: String!) {
    uploadFile(data: { file: $file, folderName: $folderName }) {
      url
    }
  }
`;

const CREATE_PRODUCT = gql`
  mutation createProductByShopAdmin(
    $title: String!
    $subMenu: ID
    $attributes: [AttributeItemInput]
    $reqCarTypes: [ID!]!
    $price: Float!
    $promotion: ProductPromotionInput
    $description: String
    $photoUrl: [String]
    $stock: Int
  ) {
    createProductByShopAdmin(
      input: {
        title: $title
        subMenu: $subMenu
        attributes: $attributes
        reqCarTypes: $reqCarTypes
        price: $price
        promotion: $promotion
        description: $description
        photoUrl: $photoUrl
        stock: $stock
      }
    ) {
      _id
      shop {
        shopMenu {
          _id
        }
      }
    }
  }
`;

const UPDATE_PRODUCT = gql`
  mutation updateProductByShopAdmin(
    $id: ID!
    $title: String!
    $subMenu: ID
    $attributes: [AttributeItemInput]
    $reqCarTypes: [ID!]!
    $price: Float!
    $promotion: ProductPromotionInput
    $description: String
    $photoUrl: [String]
    $stock: Int
  ) {
    updateProductByShopAdmin(
      id: $id
      input: {
        title: $title
        subMenu: $subMenu
        attributes: $attributes
        reqCarTypes: $reqCarTypes
        price: $price
        promotion: $promotion
        description: $description
        photoUrl: $photoUrl
        stock: $stock
      }
    ) {
      _id
    }
  }
`;

const IMAGE_SLOTS = [
  { label: "Main photo" },
  { label: "Photo 2" },
  { label: "Photo 3" },
  { label: "Photo 4" },
];

const readableError = (error) => {
  if (!error) return "";
  if (error.graphQLErrors && error.graphQLErrors.length > 0) {
    return error.graphQLErrors[0].message;
  }
  return error.message || "Something went wrong, please try again.";
};

const snapshotOf = (state) => JSON.stringify(state);

/* ------------------------------------------------------------------ *
 * Image slot — one dropzone. Used four times instead of four near-copies
 * of the same ~60 lines (main/second/third/fourth photo) the pre-redesign
 * form had, each with its own state, error flag and spinner.
 * ------------------------------------------------------------------ */

const ImageSlot = ({ label, url, uploading, onUpload, onRemove, disabled }) => (
  <Files
    maxSize="10mb"
    multiple={false}
    convertToBase64
    accept={["image/jpg", "image/jpeg", "image/png"]}
    onSuccess={(files) => onUpload(files[0])}
    disabled={disabled}
  >
    {({ browseFiles, getDropZoneProps }) => (
      <div {...getDropZoneProps({ className: `productImageSlot ${url ? "hasImage" : ""}` })}>
        {uploading ? (
          <div className="productImageUploading">
            <CircularProgress size={22} style={{ color: "var(--color-accent-primary)" }} />
          </div>
        ) : url ? (
          <React.Fragment>
            <img src={`${graphqlUrl}${url}`} alt={label} />
            <div className="productImageOverlay">
              <button type="button" onClick={browseFiles}>
                Replace
              </button>
              <button type="button" onClick={onRemove}>
                Remove
              </button>
            </div>
          </React.Fragment>
        ) : (
          <button type="button" className="productImageAdd" onClick={browseFiles}>
            <AddPhotoAlternateIcon />
            <span>{label}</span>
          </button>
        )}
      </div>
    )}
  </Files>
);

/* ------------------------------------------------------------------ *
 * Component
 * ------------------------------------------------------------------ */

const ShopProductForm = ({ product, subMenuId: forcedSubMenuId }) => {
  const theme = useContext(ThemeContext);
  const isFood = theme === "Restaurant";
  const noun = isFood ? "food" : "product";
  const isEditing = Boolean(product);

  const { data: contextData, loading: contextLoading, error: contextError } = useQuery(GET_FORM_CONTEXT, {
    fetchPolicy: "network-only",
  });

  const shop = contextData && contextData.getShopByShopAdmin;
  const rootCategoryId = shop && shop.rootCategory && shop.rootCategory._id;
  const categoryLabel = shop && shop.rootCategory && shop.rootCategory.title === "Restaurant" ? "Submenu" : "Category";

  const { data: attributeGroupsData } = useQuery(GET_ATTRIBUTE_GROUPS, {
    variables: { rootCategory: rootCategoryId },
    skip: !rootCategoryId,
  });

  const [uploadFile] = useMutation(UPLOAD_FILE);
  const [createProduct, { loading: creating }] = useMutation(CREATE_PRODUCT);
  const [updateProduct, { loading: updating }] = useMutation(UPDATE_PRODUCT);
  const submitting = creating || updating;

  /* ---------------- form state ---------------- */

  const [title, setTitle] = useState(product ? product.title : "");
  const [description, setDescription] = useState(product ? product.description || "" : "");
  const [price, setPrice] = useState(product ? String(product.price) : "");
  const [stock, setStock] = useState(product ? String(product.stock) : "0");
  const [subMenuId, setSubMenuId] = useState(product && product.subMenuId ? product.subMenuId : "");

  const [carTypeId, setCarTypeId] = useState(
    product && product.reqCarTypes && product.reqCarTypes.length > 0 ? product.reqCarTypes[0]._id : ""
  );

  const [images, setImages] = useState(() => {
    const urls = product && product.photoUrl ? product.photoUrl : [];
    return [urls[0] || "", urls[1] || "", urls[2] || "", urls[3] || ""];
  });
  const [uploadingSlot, setUploadingSlot] = useState(-1);
  const [imagesError, setImagesError] = useState("");

  const [hasPromotion, setHasPromotion] = useState(
    Boolean(product && product.promotion && product.promotion.percent)
  );
  const [percent, setPercent] = useState(
    product && product.promotion && product.promotion.percent ? String(product.promotion.percent) : ""
  );
  const [startDate, setStartDate] = useState(
    product && product.promotion && product.promotion.discountFrom
      ? moment(product.promotion.discountFrom).format("YYYY-MM-DDTHH:mm")
      : ""
  );
  const [endDate, setEndDate] = useState(
    product && product.promotion && product.promotion.discountTo
      ? moment(product.promotion.discountTo).format("YYYY-MM-DDTHH:mm")
      : ""
  );

  const [attributes, setAttributes] = useState({});

  useEffect(() => {
    if (!attributeGroupsData) return;
    const next = {};
    attributeGroupsData.getAttributeGroups.forEach((group) => {
      const existing =
        product && product.attributes
          ? product.attributes.find((a) => a.attributeGroup._id === group._id)
          : null;
      next[group._id] = existing ? existing.att.map((a) => a._id) : [];
    });
    setAttributes(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributeGroupsData]);

  useEffect(() => {
    if (!carTypeId && contextData && contextData.getReqCarTypes.length > 0) {
      setCarTypeId(contextData.getReqCarTypes[0]._id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextData]);

  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState("");

  /* ---------------- unsaved-changes guard ---------------- */

  const currentSnapshot = () =>
    snapshotOf({ title, description, price, stock, subMenuId, carTypeId, images, hasPromotion, percent, startDate, endDate, attributes });

  const initialSnapshotRef = useRef(null);
  useEffect(() => {
    // Deferred until attribute groups load so a default-empty {} doesn't
    // get baked into the "pristine" snapshot and then immediately looks
    // dirty the moment the real per-group defaults are set.
    if (attributeGroupsData && initialSnapshotRef.current === null) {
      initialSnapshotRef.current = currentSnapshot();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributeGroupsData]);

  const isDirty = initialSnapshotRef.current !== null && currentSnapshot() !== initialSnapshotRef.current;

  useEffect(() => {
    window.onbeforeunload = isDirty ? () => true : undefined;
    return () => {
      window.onbeforeunload = undefined;
    };
  }, [isDirty]);

  /* ---------------- image upload ---------------- */

  const handleUpload = (index, file) => {
    setUploadingSlot(index);
    setImagesError("");
    uploadFile({ variables: { file: file.src.file, folderName: "" } })
      .then((res) => {
        setImages((current) => {
          const next = [...current];
          next[index] = res.data.uploadFile.url;
          return next;
        });
        setUploadingSlot(-1);
      })
      .catch(() => {
        setUploadingSlot(-1);
        setImagesError("That photo couldn't be uploaded — try a smaller file.");
      });
  };

  const handleRemoveImage = (index) => {
    setImages((current) => {
      const next = [...current];
      next[index] = "";
      return next;
    });
  };

  /* ---------------- attributes ---------------- */

  const toggleAttribute = (groupId, attributeId) => {
    setAttributes((current) => {
      const selected = current[groupId] || [];
      return {
        ...current,
        [groupId]: selected.includes(attributeId)
          ? selected.filter((id) => id !== attributeId)
          : [...selected, attributeId],
      };
    });
  };

  /* ---------------- validation + submit ---------------- */

  const onCancel = () => {
    window.onbeforeunload = undefined;
    window.location.href = endPoint + (isFood ? "/shopAdmin/foods" : "/shopAdmin/products");
  };

  const validate = () => {
    const errors = {};
    if (!title.trim()) errors.title = "Give it a title.";

    const priceNum = parseFloat(price);
    if (!price || Number.isNaN(priceNum) || priceNum <= 0) errors.price = "Enter a price greater than 0.";

    if (stock && !/^\d+$/.test(String(stock).trim())) {
      errors.stock = "Stock must be a whole number, 0 or more.";
    }

    if (!description.trim()) errors.description = "Add a description.";

    if (!images.some(Boolean)) errors.images = "Upload at least one photo.";

    if (hasPromotion) {
      const pct = parseFloat(percent);
      if (!percent || Number.isNaN(pct) || pct <= 0 || pct > 100) {
        errors.percent = "Enter a percentage between 1 and 100.";
      }
      if (!startDate) {
        errors.startDate = "Pick a start date.";
      } else if (!isEditing && moment(startDate).isBefore(moment())) {
        errors.startDate = "Start date must be in the future.";
      }
      if (!endDate) {
        errors.endDate = "Pick an end date.";
      } else if (startDate && !moment(endDate).isAfter(moment(startDate))) {
        errors.endDate = "End date must be after the start date.";
      }
    }

    return errors;
  };

  const onSubmit = () => {
    if (submitting) return;
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setFormError("");

    const variables = {
      title: title.trim(),
      description: description.trim(),
      price: Number(price),
      stock: stock && /^\d+$/.test(String(stock).trim()) ? parseInt(stock, 10) : 0,
      subMenu: forcedSubMenuId || subMenuId || undefined,
      reqCarTypes: carTypeId ? [carTypeId] : [],
      attributes: Object.keys(attributes)
        .filter((groupId) => attributes[groupId].length > 0)
        .map((groupId) => ({ attributeGroup: groupId, att: attributes[groupId] })),
      promotion: hasPromotion
        ? {
            percent: parseInt(percent, 10),
            discountFrom: moment(startDate).utc(),
            discountTo: moment(endDate).utc(),
          }
        : undefined,
      photoUrl: images.filter(Boolean),
    };

    window.onbeforeunload = undefined;

    const goToList = () => {
      window.location.href = endPoint + (isFood ? "/shopAdmin/foods" : "/shopAdmin/products");
    };

    if (isEditing) {
      updateProduct({ variables: { id: product._id, ...variables } })
        .then(goToList)
        .catch((err) => setFormError(readableError(err)));
    } else {
      createProduct({ variables })
        .then((res) => {
          if (forcedSubMenuId || res.data.createProductByShopAdmin.shop.shopMenu === null) {
            window.location.href =
              endPoint + `/shopAdmin/${window.localStorage.getItem("shopType") === "Restaurant" ? "Menu" : "Categories"}`;
          } else {
            goToList();
          }
        })
        .catch((err) => setFormError(readableError(err)));
    }
  };

  /* ---------------- render ---------------- */

  if (contextLoading) {
    return (
      <div className="page productFormPage">
        <main className="main">
          <PageHeader title={isEditing ? `Edit ${noun}` : `Add ${noun}`} />
          <MetricsSkeleton count={2} />
          <TableSkeleton rows={4} columns={2} />
        </main>
      </div>
    );
  }

  if (contextError) {
    return <ErrorPage />;
  }

  if (shop && shop.shopMenu === null) {
    return (
      <div className="page productFormPage">
        <main className="main">
          <EmptyState
            icon={<CategoryIcon />}
            title="Set up your menu first"
            message={`Your shop doesn't have any ${categoryLabel.toLowerCase()}s yet — add one before you can create a ${noun}.`}
            action={
              <Button
                variant="primary"
                onClick={() => {
                  window.location.href =
                    endPoint + `/shopAdmin/${window.localStorage.getItem("shopType") === "Restaurant" ? "Menu" : "Categories"}`;
                }}
              >
                Go to {categoryLabel.toLowerCase()}s
              </Button>
            }
          />
        </main>
      </div>
    );
  }

  const priceNum = parseFloat(price);
  const percentNum = parseFloat(percent);
  const discountedPrice =
    hasPromotion && !Number.isNaN(priceNum) && !Number.isNaN(percentNum)
      ? priceNum - (priceNum * percentNum) / 100
      : null;

  const subMenuOptions = (shop && shop.shopMenu && shop.shopMenu.subMenus) || [];
  const carTypes = (contextData && contextData.getReqCarTypes) || [];
  const attributeGroups = ((attributeGroupsData && attributeGroupsData.getAttributeGroups) || []).filter(
    (g) => g.attributes.length > 0
  );

  return (
    <div className="page productFormPage">
      <Prompt when={isDirty} message="You have unsaved changes, are you sure you want to leave?" />
      <main className="main">
        <PageHeader
          title={isEditing ? `Edit ${noun}` : `Add ${noun}`}
          description={
            isEditing
              ? `Update ${title || `this ${noun}`}.`
              : `Add a new ${noun} to your ${isFood ? "menu" : "catalogue"}.`
          }
        />

        <div className="productFormGrid">
          <div className="productFormColumn">
            <Panel title="Details">
              <div className="productFormFields">
                <Field label="Title" required htmlFor="productTitle" error={fieldErrors.title}>
                  <input
                    id="productTitle"
                    type="text"
                    className="dsInput"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                </Field>

                <Field label="Description" required htmlFor="productDescription" error={fieldErrors.description}>
                  <textarea
                    id="productDescription"
                    className="dsInput productDescriptionInput"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </Field>

                <div className="productFormRow">
                  <Field label="Price" required htmlFor="productPrice" error={fieldErrors.price} hint="CAD$">
                    <input
                      id="productPrice"
                      type="number"
                      min="0"
                      step="0.01"
                      className="dsInput"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                    />
                  </Field>
                  <Field label="Stock" htmlFor="productStock" error={fieldErrors.stock} hint="Leave 0 if untracked">
                    <input
                      id="productStock"
                      type="number"
                      min="0"
                      className="dsInput"
                      value={stock}
                      onChange={(e) => setStock(e.target.value)}
                    />
                  </Field>
                </div>

                {!forcedSubMenuId && (
                  <Field label={categoryLabel} htmlFor="productSubMenu" hint={`Which ${categoryLabel.toLowerCase()} this ${noun} appears under.`}>
                    <select
                      id="productSubMenu"
                      className="dsInput"
                      value={subMenuId}
                      onChange={(e) => setSubMenuId(e.target.value)}
                    >
                      <option value="">No {categoryLabel.toLowerCase()}</option>
                      {subMenuOptions.map((opt) => (
                        <option key={opt._id} value={opt._id}>
                          {opt.name}
                        </option>
                      ))}
                    </select>
                  </Field>
                )}
              </div>
            </Panel>

            <Panel
              title="Discount"
              subtitle="Run a limited-time percentage off this item."
              actions={
                <label className="productPromotionToggle">
                  <input
                    type="checkbox"
                    checked={hasPromotion}
                    onChange={() => setHasPromotion((v) => !v)}
                  />
                  Enable
                </label>
              }
            >
              {hasPromotion ? (
                <div className="productFormFields">
                  <Field label="Discount percentage" required htmlFor="productPercent" error={fieldErrors.percent}>
                    <input
                      id="productPercent"
                      type="number"
                      min="1"
                      max="100"
                      className="dsInput"
                      value={percent}
                      onChange={(e) => setPercent(e.target.value)}
                    />
                  </Field>
                  <div className="productFormRow">
                    <Field label="Starts" required htmlFor="productStart" error={fieldErrors.startDate}>
                      <input
                        id="productStart"
                        type="datetime-local"
                        className="dsInput"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                      />
                    </Field>
                    <Field label="Ends" required htmlFor="productEnd" error={fieldErrors.endDate}>
                      <input
                        id="productEnd"
                        type="datetime-local"
                        className="dsInput"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                      />
                    </Field>
                  </div>
                  {discountedPrice !== null && (
                    <div className="productDiscountPreview">
                      <span className="productDiscountOld">${priceNum.toFixed(2)}</span>
                      <span className="productDiscountNew">${discountedPrice.toFixed(2)}</span>
                      <span className="productDiscountPercent">{percent || 0}% off</span>
                    </div>
                  )}
                </div>
              ) : (
                <p className="productPanelHint">No discount is running on this {noun}.</p>
              )}
            </Panel>
          </div>

          <div className="productFormColumn">
            <Panel title="Photos" subtitle="The first photo is used as the main image.">
              <div className="productImageGrid">
                {IMAGE_SLOTS.map((slot, index) => (
                  <ImageSlot
                    key={index}
                    label={slot.label}
                    url={images[index]}
                    uploading={uploadingSlot === index}
                    disabled={uploadingSlot !== -1}
                    onUpload={(file) => handleUpload(index, file)}
                    onRemove={() => handleRemoveImage(index)}
                  />
                ))}
              </div>
              {(fieldErrors.images || imagesError) && (
                <span className="dsFieldError">{fieldErrors.images || imagesError}</span>
              )}
              <p className="productPanelHint">JPG or PNG, up to 10MB each.</p>
            </Panel>

            {carTypes.length > 0 && (
              <Panel title="Delivery vehicle" subtitle="Which vehicle type can deliver this item.">
                <div className="productOptionGrid">
                  {carTypes.map((carType) => (
                    <label
                      key={carType._id}
                      className={`dsOptionCard ${carTypeId === carType._id ? "isSelected" : ""}`}
                    >
                      <input
                        type="radio"
                        name="carType"
                        checked={carTypeId === carType._id}
                        onChange={() => setCarTypeId(carType._id)}
                      />
                      <span className="dsOptionCardText">
                        <strong>{carType.name && carType.name.toLowerCase()}</strong>
                      </span>
                    </label>
                  ))}
                </div>
              </Panel>
            )}

            {attributeGroups.map((group) => (
              <Panel key={group._id} title={group.name.toLowerCase()}>
                <div className="productOptionGrid">
                  {group.attributes.map((attribute) => {
                    const selected = (attributes[group._id] || []).includes(attribute._id);
                    return (
                      <label key={attribute._id} className={`dsOptionCard ${selected ? "isSelected" : ""}`}>
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleAttribute(group._id, attribute._id)}
                        />
                        <span className="dsOptionCardText">
                          <strong>{attribute.name}</strong>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </Panel>
            ))}
          </div>
        </div>

        {formError && (
          <div className="dsFormMessage error" role="alert">
            {formError}
          </div>
        )}

        <div className="productFormActions">
          <Button onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onSubmit} loading={submitting}>
            {isEditing ? "Save changes" : `Create ${noun}`}
          </Button>
        </div>
      </main>
    </div>
  );
};

export default ShopProductForm;
